import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import shuffle from 'utils/shuffle';
import { Section, Container, Typography } from 'components/base';
import useDataContext from 'data/context/data';
import contentApi from 'data/api/content';

import { Head, Layout } from '../components/layout';

import ProductDetailPage from './product/[productId]';
import CategoryDetailPage from './category/[categoryId]';
import ContentPage, { CONTENT_PAGES } from './content/[contentId]';

import { ProductsSection } from '../components/custom/mainSections';
import { MIGHT_LIKE_PRODUCTS } from '../specifications/lists';

import PRODUCT_SPECIFICATION from '../specifications/products';
import PRODUCT_CATEGORIES from '../specifications/categories';

const PAGE_TYPE = {
  NOT_FOUND: -1,
  PRODUCT: 0,
  CATEGORY: 1,
  CONTENT: 2,
};

const PRODUCT_URLS = Object.entries(PRODUCT_SPECIFICATION).reduce((acc, [id, { url }]) => {
  acc[url] = id;
  return acc;
}, {});

const CATEGORY_URLS = PRODUCT_CATEGORIES.reduce((acc, { id, url }) => {
  acc[url] = id;
  return acc;
}, {});

const DynamicPage = props => {
  const { pageType, ...pageProps } = props;

  const dataContext = useDataContext();

  const [mightLikeProducts, setMightLikeProducts] = useState([]);
  useEffect(() => {
    if (props.pageType === PAGE_TYPE.NOT_FOUND) {
      const products = dataContext.getProducts(MIGHT_LIKE_PRODUCTS);
      setMightLikeProducts(shuffle(products).slice(0, 6));
    }
  }, [props.pageType]);

  if (pageType === PAGE_TYPE.PRODUCT) {
    return <ProductDetailPage {...pageProps} />;
  }

  if (pageType === PAGE_TYPE.CATEGORY) {
    return <CategoryDetailPage {...pageProps} />;
  }

  if (pageType === PAGE_TYPE.CONTENT) {
    return <ContentPage {...pageProps} />;
  }

  return (
    <>
      <Head title="Page Not Found" />

      <Layout>
        <Section>
          <Container>
            <Typography.H2 className="section-title mb-2">
              Oh no! We’re sorry we aren’t able to find this page.
            </Typography.H2>
          </Container>
        </Section>

        <ProductsSection title="You might like" products={mightLikeProducts} xs={12} md={4} />
      </Layout>
    </>
  );
};

DynamicPage.getInitialProps = async ctx => {
  const params = ctx.query.params || [];

  const url = `/${params.join('/')}`;

  //
  // 1. ProductDetailPage
  //
  if (PRODUCT_URLS[url]) {
    const productId = PRODUCT_URLS[url];

    const content = await contentApi.product(productId);

    return {
      pageType: PAGE_TYPE.PRODUCT,
      productId,
      content,
    };
  }

  //
  // 2. CategoryDetailPage
  //
  if (CATEGORY_URLS[url]) {
    const categoryId = CATEGORY_URLS[url];

    const content = await contentApi.category(categoryId);

    return {
      pageType: PAGE_TYPE.CATEGORY,
      categoryId,
      content,
    };
  }

  //
  // 3. ContentPage
  //
  if (CONTENT_PAGES[url]) {
    const contentId = CONTENT_PAGES[url];

    // TODO no description
    const {
      page_title: pageTitle,
      image_key: imageKey,
      body,
      description,
      body1,
      page_caption: pageCaption,
    } = await contentApi.singleType(contentId);

    return {
      pageType: PAGE_TYPE.CONTENT,
      contentId,
      imageKey,
      pageTitle,
      pageCaption,
      body: body || description,
      slices: body1,
    };
  }

  if (ctx.res) {
    ctx.res.statusCode = 404;
  }

  return { pageType: PAGE_TYPE.NOT_FOUND };
};

DynamicPage.propTypes = {
  pageType: PropTypes.number,
};

DynamicPage.defaultProps = {
  pageType: -1,
};

export default DynamicPage;
